/**
 * Statistiken (F-02) - reine Rechnungen, getestet. Die Diagramme lesen nur
 * noch ab, was hier herauskommt.
 */
import { addDays, differenceInDays, isoWeek, startOfWeek, toLocalDate } from '@/lib/datetime';
import type { TagesPunkt } from '@/domain/habits/daily';
import type { LifeWheelSnapshot, LocalDate, Task } from '@/domain/types';

export interface WochenWert {
  /** Montag der Woche. */
  start: LocalDate;
  kw: number;
  anzahl: number;
}

/** ISO-Zeitpunkt -> lokaler Kalendertag; kaputte Werte zaehlen nicht. */
const tagVon = (iso: string): LocalDate | undefined => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : toLocalDate(d);
};

/**
 * Erledigte Aufgaben je Kalenderwoche (Mo-So): `wochen` Wochen, die aelteste
 * zuerst, die laufende zuletzt. Wochen ohne Erledigtes stehen mit 0 da.
 */
export function erledigtJeWoche(tasks: Task[], heute: LocalDate, wochen: number): WochenWert[] {
  const erste = addDays(startOfWeek(heute), -7 * (wochen - 1));
  const werte = Array.from({ length: wochen }, (_, i) => {
    const start = addDays(erste, 7 * i);
    return { start, kw: isoWeek(start), anzahl: 0 };
  });
  for (const t of tasks) {
    if (t.status !== 'erledigt' || !t.completedAt) continue;
    const tag = tagVon(t.completedAt);
    if (!tag || tag < erste || tag > heute) continue;
    werte[Math.floor(differenceInDays(erste, tag) / 7)]!.anzahl += 1;
  }
  return werte;
}

/** Durchschnitt je Woche, auf eine Nachkommastelle. */
export function schnitt(werte: WochenWert[]): number {
  if (werte.length === 0) return 0;
  return Math.round((werte.reduce((s, w) => s + w.anzahl, 0) / werte.length) * 10) / 10;
}

/**
 * Anteil der geschafften Gewohnheiten ueber einen Verlauf - gezaehlt wird,
 * was vorgesehen war. `null`, wenn in dem Zeitraum nichts vorgesehen war.
 */
export function gesamtQuote(punkte: Pick<TagesPunkt, 'erledigt' | 'vorgesehen'>[]): number | null {
  const soll = punkte.reduce((s, p) => s + p.vorgesehen, 0);
  if (soll === 0) return null;
  return punkte.reduce((s, p) => s + p.erledigt, 0) / soll;
}

/** Die letzten `anzahl` Lebensrad-Bewertungen bis heute, die aelteste zuerst. */
export function lebensradVerlauf(snapshots: LifeWheelSnapshot[], heute: LocalDate, anzahl: number): LifeWheelSnapshot[] {
  return snapshots
    .filter((s) => s.date <= heute)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-anzahl);
}
